import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import {
  listConceptUsage,
  suggestConceptPlacements,
  applyConceptPlacement,
  previewConceptPlacement,
  type PlacementSuggestion,
  type ConceptUsageRow,
} from "@/lib/concept-embed.functions";

export const Route = createFileRoute("/admin/concept-embed")({
  head: () => ({
    meta: [
      { title: "Kavram gömme" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ConceptEmbedPage,
});

function RelevancePill({ r }: { r: PlacementSuggestion["relevance"] }) {
  const map: Record<string, string> = {
    high: "bg-emerald-600/10 text-emerald-700 border-emerald-600/20",
    medium: "bg-amber-500/10 text-amber-700 border-amber-500/20",
    low: "bg-ink/5 text-ink/60 border-ink/10",
  };
  const label = r === "high" ? "yüksek" : r === "medium" ? "orta" : "düşük";
  return (
    <span
      className={`inline-block text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded border ${map[r]}`}
    >
      {label}
    </span>
  );
}

function ConceptCard({ c, totalPosts }: { c: ConceptUsageRow; totalPosts: number }) {
  const suggest = useServerFn(suggestConceptPlacements);
  const apply = useServerFn(applyConceptPlacement);
  const preview = useServerFn(previewConceptPlacement);
  const [previews, setPreviews] = useState<Record<string, { beforeTail: string; addedHtml: string; bodyHash: string; paragraph: string }>>({});
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<PlacementSuggestion[] | null>(null);
  const [applied, setApplied] = useState<Record<string, "done" | "loading" | "error">>({});
  const [edited, setEdited] = useState<Record<string, string>>({});

  async function loadSuggestions() {
    setOpen(true);
    if (suggestions) return;
    setLoading(true);
    setError(null);
    try {
      const res = await suggest({ data: { conceptId: c.id } });
      setSuggestions(res.suggestions);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  async function previewOne(s: PlacementSuggestion) {
    const paragraph = edited[s.postId] ?? s.paragraph;
    setError(null);
    try {
      const res = await preview({ data: { postId: s.postId, conceptId: c.id, paragraph } });
      setPreviews((m) => ({ ...m, [s.postId]: { ...res, paragraph } }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function applyOne(s: PlacementSuggestion) {
    const key = s.postId;
    const pv = previews[key];
    if (!pv || pv.paragraph !== (edited[key] ?? s.paragraph)) return;
    setApplied((m) => ({ ...m, [key]: "loading" }));
    try {
      await apply({ data: { postId: s.postId, conceptId: c.id, paragraph: pv.paragraph, bodyHash: pv.bodyHash, confirmed: true } });
      setApplied((m) => ({ ...m, [key]: "done" }));
    } catch (e) {
      setApplied((m) => ({ ...m, [key]: "error" }));
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  const coverage = totalPosts === 0 ? 0 : Math.round((c.usedIn.length / totalPosts) * 100);

  return (
    <div className="rounded-md border border-border bg-paper">
      <div className="flex items-start justify-between gap-3 p-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Link
              to="/sozluk/$slug"
              params={{ slug: c.id }}
              className="font-serif text-lg hover:underline"
            >
              {c.label}
            </Link>
            <span className="text-[10px] uppercase tracking-[0.18em] text-ink/50">
              {c.usedIn.length}/{totalPosts} yazı ({coverage}%)
            </span>
          </div>
          <p className="text-xs text-ink/60 mt-1">
            {c.missingCount === 0
              ? "Tüm yayınlanmış yazılarda geçiyor."
              : `${c.missingCount} yazıda henüz bu kavram geçmiyor.`}
          </p>
        </div>
        <button
          onClick={loadSuggestions}
          disabled={c.missingCount === 0 || loading}
          className="text-xs px-3 py-1.5 rounded border border-ink/15 hover:bg-ink/5 disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
        >
          {loading ? "AI düşünüyor…" : open ? "Öneriler" : "AI ile öner"}
        </button>
      </div>

      {open && (
        <div className="border-t border-border px-4 py-3 space-y-3">
          {error && <p className="text-xs text-clay">{error}</p>}
          {loading && !suggestions && (
            <p className="text-xs text-ink/50">Uygun yazılar aranıyor…</p>
          )}
          {suggestions && suggestions.length === 0 && (
            <p className="text-xs text-ink/60">
              Bu kavram için organik bir yer bulunamadı. Yeni bir yazı yazmayı düşünebilirsin.
            </p>
          )}
          {suggestions?.map((s) => {
            const state = applied[s.postId];
            const paragraph = edited[s.postId] ?? s.paragraph;
            return (
              <div key={s.postId} className="rounded border border-border bg-white/60 p-3">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <Link
                    to="/yazilar/$slug"
                    params={{ slug: s.slug }}
                    className="text-sm font-medium hover:underline min-w-0 truncate"
                  >
                    {s.title}
                  </Link>
                  <RelevancePill r={s.relevance} />
                </div>
                <p className="text-[11px] text-ink/60 mb-2">{s.why}</p>
                <textarea
                  value={paragraph}
                  onChange={(e) =>
                    setEdited((m) => ({ ...m, [s.postId]: e.target.value }))
                  }
                  rows={3}
                  className="w-full text-xs border border-ink/10 rounded p-2 bg-white font-sans"
                />
                {previews[s.postId] && previews[s.postId].paragraph === paragraph && (
                  <div className="mt-2 grid gap-2 text-[11px] sm:grid-cols-2">
                    <div className="rounded border border-border p-2">
                      <p className="mb-1 font-semibold">Önce (yazının sonu)</p>
                      <pre className="whitespace-pre-wrap break-words font-sans text-ink/70">…{previews[s.postId].beforeTail}</pre>
                    </div>
                    <div className="rounded border border-border p-2">
                      <p className="mb-1 font-semibold">Eklenecek</p>
                      <pre className="whitespace-pre-wrap break-words font-sans">{previews[s.postId].addedHtml}</pre>
                    </div>
                  </div>
                )}
                <div className="mt-2 flex items-center justify-between gap-2">
                  <span className="text-[10px] text-ink/50">
                    Önce önizleyin; onaylarsanız yazının sonuna eklenir ve değişiklik geçmişine kaydedilir.
                  </span>
                  <button
                    onClick={() => previewOne(s)}
                    disabled={state === "loading" || state === "done"}
                    className="text-xs px-3 py-1 rounded border border-ink/15 hover:bg-ink/5 disabled:opacity-40"
                  >
                    Önizle
                  </button>
                  <button
                    onClick={() => applyOne(s)}
                    disabled={state === "loading" || state === "done" || !previews[s.postId] || previews[s.postId].paragraph !== paragraph}
                    className="text-xs px-3 py-1 rounded bg-ink text-paper hover:bg-ink/80 disabled:opacity-40"
                  >
                    {state === "done"
                      ? "✓ Eklendi"
                      : state === "loading"
                        ? "Ekleniyor…"
                        : state === "error"
                          ? "Tekrar dene"
                          : "Onayla ve ekle"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ConceptEmbedPage() {
  const load = useServerFn(listConceptUsage);
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "concept-embed"],
    queryFn: () => load(),
  });

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-serif text-2xl">Kavram gömme</h1>
        <p className="text-sm text-ink/60 mt-1 max-w-2xl">
          Sözlükteki her pilot kavramın hangi yazılarda geçtiğini gör; AI, kavramın doğal
          biçimde eklenebileceği makaleleri önersin. Hiçbir öneri kendiliğinden yazıya eklenmez: önce önce/sonra
          farkını gör, onaylarsan eklenir ve kim/ne zaman/ne değişti kaydedilir.
        </p>
      </header>

      {isLoading && <p className="text-sm text-ink/60">Yükleniyor…</p>}

      {data && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.concepts.map((c) => (
            <ConceptCard key={c.id} c={c} totalPosts={data.totalPosts} />
          ))}
        </div>
      )}
    </div>
  );
}
